import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { awardXP, getTodayChallengeGame, completeDailyChallenge } from '../utils/xp';
import { loadGamePool, pickGameWords } from '../data/gameWords';
import { useLanguage } from '../i18n/LanguageContext';
import './FlashcardSprint.css';

const TEXT = {
    loading: { en: 'Loading words...', it: 'Caricamento parole...', es: 'Cargando palabras...', fr: 'Chargement des mots...', pt: 'Carregando palavras...' },
    complete: { en: 'Sprint complete!', it: 'Sprint completato!', es: '¡Sprint completado!', fr: 'Sprint terminé !', pt: 'Sprint concluído!' },
    bestStreak: { en: 'Best streak', it: 'Serie migliore', es: 'Mejor racha', fr: 'Meilleure série', pt: 'Melhor sequência' },
    review: { en: 'Review these', it: 'Da ripassare', es: 'Para repasar', fr: 'À revoir', pt: 'Para revisar' },
    again: { en: 'Play again →', it: 'Gioca ancora →', es: 'Jugar otra vez →', fr: 'Rejouer →', pt: 'Jogar de novo →' },
    back: { en: 'Back to Games', it: 'Torna ai giochi', es: 'Volver a los juegos', fr: 'Retour aux jeux', pt: 'Voltar aos jogos' },
    notEnough: {
        en: 'Not enough words yet. Add some to your Word Bank!',
        it: 'Non ci sono ancora abbastanza parole. Aggiungine qualcuna al tuo Word Bank!',
        es: 'Aún no hay suficientes palabras. ¡Añade algunas a tu Word Bank!',
        fr: "Pas encore assez de mots. Ajoutez-en à votre Word Bank !",
        pt: 'Ainda não há palavras suficientes. Adicione algumas ao seu Word Bank!',
    },
    timeUp: { en: "⏱ Time's up! It was", it: '⏱ Tempo scaduto! Era', es: '⏱ ¡Se acabó el tiempo! Era', fr: '⏱ Temps écoulé ! C’était', pt: '⏱ O tempo acabou! Era' },
};

const ROUND_TIME = 8;
const TOTAL_ROUNDS = 12;

function shuffle(arr) {
    return [...arr].sort(() => Math.random() - 0.5);
}

export default function FlashcardSprint() {
    const navigate = useNavigate();
    const { interfaceLanguage } = useLanguage();
    const uiLang = interfaceLanguage || 'en';
    const tx = (key) => (TEXT[key] && (TEXT[key][uiLang] || TEXT[key].en)) || key;
    const [pool, setPool] = useState([]);
    const [loading, setLoading] = useState(true);
    const [round, setRound] = useState(0);
    const [current, setCurrent] = useState(null);
    const [options, setOptions] = useState([]);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [timeLeft, setTimeLeft] = useState(ROUND_TIME);
    const [selected, setSelected] = useState(null);
    const [finished, setFinished] = useState(false);
    const [missed, setMissed] = useState([]);
    const timerRef = useRef(null);

    useEffect(() => {
        const init = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            const level = localStorage.getItem('vozoly_level') || 'A1';
            const lang = (localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
            const gamePool = await loadGamePool(user ? user.id : null, lang, interfaceLanguage, level);
            // Flashcards need a translation, and very long phrases do not fit on a button
            const usable = w => w.word && w.translation && w.word.length <= 40 && w.translation.length <= 60;
            const seen = new Set();
            const unique = pickGameWords(gamePool, TOTAL_ROUNDS, usable).filter(w => {
                const key = w.word.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            setPool(unique);
            setLoading(false);
        };
        init();
    }, [interfaceLanguage]);

    const buildRound = useCallback((index, poolArr) => {
        if (index >= poolArr.length) return;
        const word = poolArr[index];
        const distractors = shuffle(poolArr.filter(w => w.word !== word.word)).slice(0, 3);
        const allOptions = shuffle([word, ...distractors]);
        setCurrent(word);
        setOptions(allOptions);
        setSelected(null);
        setTimeLeft(ROUND_TIME);
    }, []);

    useEffect(() => {
        if (!loading && pool.length > 0) buildRound(0, pool);
    }, [loading, pool, buildRound]);

    const finishGame = useCallback(() => {
        setFinished(true);
        const xp = Math.min(20, score * 2);
        if (xp > 0) awardXP(xp, 'flashcard-sprint');
        if (score >= Math.ceil(pool.length * 0.6) && getTodayChallengeGame() === 'flashcard-sprint') {
            completeDailyChallenge('flashcard-sprint');
        }
    }, [score, pool.length]);

    const nextRound = useCallback(() => {
        const nextIndex = round + 1;
        if (nextIndex >= pool.length) {
            finishGame();
            return;
        }
        setRound(nextIndex);
        buildRound(nextIndex, pool);
    }, [round, pool, buildRound, finishGame]);

    const handleTimeout = useCallback(() => {
        setSelected('timeout');
        setStreak(0);
        setMissed(prev => [...prev, current]);
        setTimeout(nextRound, 900);
    }, [current, nextRound]);

    useEffect(() => {
        if (finished || loading || !current) return;
        timerRef.current = setInterval(() => {
            setTimeLeft(t => {
                if (t <= 1) {
                    clearInterval(timerRef.current);
                    handleTimeout();
                    return 0;
                }
                return t - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [current, finished, loading, handleTimeout]);

    const handleAnswer = (option) => {
        if (selected) return;
        clearInterval(timerRef.current);
        setSelected(option.word);
        if (option.word === current.word) {
            setScore(s => s + 1);
            setStreak(s => {
                const next = s + 1;
                setBestStreak(b => Math.max(b, next));
                return next;
            });
        } else {
            setStreak(0);
            setMissed(prev => [...prev, current]);
        }
        setTimeout(nextRound, 700);
    };

    const playAgain = () => {
        setScore(0);
        setStreak(0);
        setBestStreak(0);
        setRound(0);
        setFinished(false);
        setMissed([]);
        setPool(prev => shuffle(prev));
    };

    if (loading) {
        return <div className="fs-screen"><div className="fs-loading">{tx('loading')}</div></div>;
    }

    if (finished) {
        return (
            <div className="fs-screen">
                <div className="fs-complete">
                    <div className="fs-complete-icon">⚡</div>
                    <div className="fs-complete-title">{tx('complete')}</div>
                    <div className="fs-complete-score">{score} / {pool.length}</div>
                    <div className="fs-complete-stat">{tx('bestStreak')}: {bestStreak} 🔥</div>
                    <div className="fs-complete-xp">+{Math.min(20, score * 2)} XP</div>

                    {missed.length > 0 && (
                        <div className="fs-missed">
                            <div className="fs-missed-label">{tx('review')}</div>
                            {missed.slice(0, 6).map((w, i) => (
                                <div key={i} className="fs-missed-row">
                                    <span className="fs-missed-word">{w.word}</span>
                                    <span className="fs-missed-translation">{w.translation}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <button className="fs-play-again" onClick={playAgain}>{tx('again')}</button>
                    <button className="fs-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    if (!current || pool.length < 4) {
        return <div className="fs-screen"><div className="fs-loading">{tx('notEnough')}</div></div>;
    }

    return (
        <div className="fs-screen">
            <div className="fs-top-bar">
                <button className="fs-back" onClick={() => navigate('/games')}>←</button>
                <div className="fs-progress">{round + 1} / {pool.length}</div>
                <div className="fs-streak">{streak > 1 ? `🔥 ${streak}` : ''}</div>
            </div>

            <div className="fs-timer-track">
                <div className="fs-timer-fill" style={{ width: `${(timeLeft / ROUND_TIME) * 100}%` }}></div>
            </div>

            <div className="fs-word-card">
                <div className="fs-word">{current.word}</div>
            </div>

            <div className="fs-options">
                {options.map(opt => {
                    let cls = 'fs-option';
                    if (selected) {
                        if (opt.word === current.word) cls += ' correct';
                        else if (opt.word === selected) cls += ' wrong';
                        else cls += ' faded';
                    }
                    return (
                        <button key={opt.word} className={cls} onClick={() => handleAnswer(opt)} disabled={!!selected}>
                            {opt.translation}
                        </button>
                    );
                })}
            </div>

            {selected === 'timeout' && (
                <div className="fs-timeout-msg">{tx('timeUp')} “{current.translation}”</div>
            )}
        </div>
    );
}